import { lazy, Suspense, useEffect, useState } from 'react';
import { CartProvider } from './contexts/CartContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AnnouncementBar } from './components/AnnouncementBar';
// import { PanchangBar } from './components/PanchangBar';
import { Header } from './components/Header';
import { HeroCarousel } from './components/HeroCarousel';
import { VideoSection } from './components/VideoSection';
import { CategoryCards } from './components/CategoryCards';
import { ProductGrid } from './components/ProductGrid';
import { CategoryGrid } from './components/CategoryGrid';
import { TrustBadges } from './components/TrustBadges';
import { Footer } from './components/Footer';
import { CartDrawer } from './components/CartDrawer';
// import { FAQPage } from './components/FAQPage';
// import { FAQSection } from './components/FAQSection';
import { ReviewsSection } from './components/ReviewsSection';
import { MaintenanceMode } from './components/MaintenanceMode';
import { WelcomePopup } from './components/WelcomePopup';
import { getCurrentUrl, navigate, normalizeLegacyHashRoute } from './lib/router';

const AdminLayout = lazy(() => import('./components/admin/AdminLayout').then((module) => ({ default: module.AdminLayout })));
const LoginPage = lazy(() => import('./components/LoginPage').then((module) => ({ default: module.LoginPage })));
const CheckoutPage = lazy(() => import('./components/CheckoutPage').then((module) => ({ default: module.CheckoutPage })));
const CategoryPage = lazy(() => import('./components/CategoryPage').then((module) => ({ default: module.CategoryPage })));
const ProductDetailPage = lazy(() => import('./components/ProductDetailPage').then((module) => ({ default: module.ProductDetailPage })));
const ContactPage = lazy(() => import('./components/ContactPage').then((module) => ({ default: module.ContactPage })));
const OrdersPage = lazy(() => import('./components/OrdersPage').then((module) => ({ default: module.OrdersPage })));
const UserProfile = lazy(() => import('./components/UserProfile').then((module) => ({ default: module.UserProfile })));
const AboutPage = lazy(() => import('./components/AboutPage').then((module) => ({ default: module.AboutPage })));
const FestivalPage = lazy(() => import('./components/FestivalPage').then((module) => ({ default: module.FestivalPage })));
const LegalPolicyPage = lazy(() => import('./components/LegalPolicyPage').then((module) => ({ default: module.LegalPolicyPage })));
const PromoBlocksSection = lazy(() => import('./components/PromoBlocksSection').then((module) => ({ default: module.PromoBlocksSection })));

function RouteLoader() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center px-4">
      <div className="text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-primary/15 border-t-primary" />
        <p className="mt-4 text-sm font-semibold tracking-[0.28em] text-primary uppercase">
          Loading
        </p>
      </div>
    </div>
  );
}

function AppContent() {
  const { settings } = useSettings();
  const [currentPath, setCurrentPath] = useState(getCurrentUrl());

  useEffect(() => {
    normalizeLegacyHashRoute();

    const handleRouteChange = () => {
      setCurrentPath(getCurrentUrl());
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  useEffect(() => {
    const anchor = window.location.hash.replace('#', '').trim();
    if (!anchor) return;

    const timeout = setTimeout(() => {
      const element = document.getElementById(anchor);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);

    return () => clearTimeout(timeout);
  }, [currentPath]);

  const renderBestSellersPage = () => (
    <>
      <AnnouncementBar />
      <Header />
      <main>
        <ProductGrid title="Best Sellers" filter="bestseller" />
      </main>
      <Footer />
      <CartDrawer />
    </>
  );

  const renderAstroPujaBookingPage = () => (
    <>
      <AnnouncementBar />
      <Header />
      <main>
        <PromoBlocksSection />
      </main>
      <Footer />
      <CartDrawer />
    </>
  );

  const renderCategoriesPage = () => (
    <>
      <AnnouncementBar />
      <Header />
      <main>
        <CategoryGrid />
      </main>
      <Footer />
      <CartDrawer />
    </>
  );

  // Check maintenance mode (allow admin access)
  if (settings?.maintenanceMode && !currentPath.includes('/admin') && currentPath !== '/') {
    return <MaintenanceMode />;
  }

  const renderPage = () => {
    const path = currentPath.split('#')[0];

    if (path === '/admin') {
      return <AdminLayout onClose={() => {
        navigate('/', true);
        setCurrentPath('/');
      }} />;
    }

    if (path === '/login') {
      return <LoginPage />;
    }

    if (path === '/checkout') {
      return <CheckoutPage />;
    }

    // if (path === '/faq') {
    //   return <FAQPage />;
    // }

    if (path === '/contact') {
      return <ContactPage />;
    }

    if (path === '/privacy' || path === '/privacy-policy') {
      return <LegalPolicyPage policyKey="privacyPolicy" />;
    }

    if (path === '/terms' || path === '/terms-and-conditions') {
      return <LegalPolicyPage policyKey="termsPolicy" />;
    }

    if (path === '/refund-policy' || path === '/refunds' || path === '/cancellation-refund-policy') {
      return <LegalPolicyPage policyKey="refundPolicy" />;
    }

    if (path === '/orders') {
      return <OrdersPage />;
    }

    if (path === '/profile') {
      return <UserProfile />;
    }

    if (path === '/about' || path.startsWith('/about')) {
      return <AboutPage />;
    }

    if (path.startsWith('/category/')) {
      const categorySlug = path.split('/')[2];
      return <CategoryPage categorySlug={categorySlug} />;
    }

    if (path.startsWith('/product/')) {
      const productSlug = path.split('/')[2];
      return <ProductDetailPage productSlug={productSlug} />;
   
 
    }

    if (path.startsWith('/festival/')) {
      const festivalSlug = path.split('/')[2];
      return <FestivalPage festivalSlug={festivalSlug} />;
    }

    if (path === '/bestsellers') {
      return renderBestSellersPage();
    }

    if (path === '/astro-puja-booking') {
      return renderAstroPujaBookingPage();
    }

    if (path === '/categories') {
      return renderCategoriesPage();
    }

    if (path.startsWith('/shop/') || path === '/products') {
      return <CategoryPage />;
    }

    return (
      <>
        <AnnouncementBar />
        {/* <PanchangBar /> */}
        <Header />

        <main>
          <HeroCarousel />
          <CategoryCards />
          <TrustBadges />
          <ProductGrid title="Best Sellers" filter="bestseller" limit={8} />
          <VideoSection />
          <ProductGrid title="Shop All" limit={8} />
          <ReviewsSection />
        </main>

        <Footer />
        <CartDrawer />
        <WelcomePopup />
      </>
    );
  };

  return (
    <CartProvider>
      <div className="min-h-screen bg-[var(--surface)]">
        <Suspense fallback={<RouteLoader />}>
          {renderPage()}
        </Suspense>
      </div>
    </CartProvider>
  );
}

function App() {
  return (
    <SettingsProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </SettingsProvider>
  );
}

export default App;
